import { FC, useReducer, useRef, useMemo } from 'react'
import { useRouter } from 'next/router'

import Auth from 'store/auth'
import MainLayout from 'containers/main-layout/main-layout'
import WidthLimiter from 'containers/width-limiter/width-limiter'
import SwitchToggle from 'components/parts/switch-toggle/switch-toggle'
import ResultsWrapper from 'components/search/search-page/results-wrapper'
import SEOMetatags from 'components/parts/seo-metatags/seo-metatags'
import SEOBreadCrumbs from 'components/parts/seo-metatags/seo-breadcrumbs'
import SearchPagination from '../search-pagination/search-pagination'
import usePageFromSelectedOption from 'hooks/usePageFromSelectedOption'
import useSpecialities from 'store/lists/useSpecialities'
import useWebinarSubscription from 'hooks/useWebinarSubscription'

import { cutHashFromURL } from 'utils/getURLData'
import { defineDopParams } from 'utils/search/defineDopParams'
import {
  normalizeData,
  getPageBC,
  getPageSEO,
  getURL,
  CARDS_ON_PAGE,
} from 'components/search/speciality-search/utils'
import {
  ToggleAliases,
  SearchOpt,
  searchNamesSpeaciality,
  SearchKey,
  request,
  reducer,
  Action,
  initData,
} from 'constants/search-data/big-search-page-data'
import { popupName } from 'constants/auth/auth-data'
import { IOptions } from 'constants/types/common.types'

import styles from 'styles/search/search-page.module.scss'

interface ISpecialitySearch {
  data: string
}

interface IProps {
  specialities: IOptions[]
  initCards: typeof initData
  searchData: ReturnType<typeof normalizeData>
}

const SpecialitySearch: FC<ISpecialitySearch> = (props) => {
  const { specialities: initSpecialities, initCards, searchData } = JSON.parse(props.data) as IProps

  useSpecialities(initSpecialities)
  const router = useRouter()
  const containerRef = useRef<HTMLDivElement>(null)
  const { isAuthorized } = Auth.useAuth()
  const { unsubscribeWebinar, subscribeWebinar } = useWebinarSubscription()

  const path = cutHashFromURL(router.asPath)
  const { searchString, selectedSort, pageId } = searchData

  const SpecialityPageSEO = getPageSEO(searchString, path, Number(router.query.page || 1))
  const SpecialityPageBC = getPageBC(searchString)

  const [data, dispatch] = useReducer(reducer, { ...initData, ...initCards, isLoaded: true })

  const { selectedOption, setSelectedOption } = usePageFromSelectedOption(
    ToggleAliases,
    `/speciality`,
    String(pageId)
  )

  const getNextResults = (key: string) => async (offset: number) => {
    dispatch({ type: Action.SET_IS_LOADING, key })
    const url = getURL(searchString, key, String(offset), selectedSort)

    request(url, (newData: any) => {
      dispatch({ type: Action.SET_ALL_DATA, payload: newData })
    })
  }

  const handleParameterSet = (param: string, value: string) => {
    const offset = Number(value) * CARDS_ON_PAGE - CARDS_ON_PAGE
    getNextResults(SearchKey[selectedOption])(offset)
  }

  const handleCardData = async (webinarId: number, isSubscribed: boolean) => {
    if (!isAuthorized)
      return router.push(`${path}#${popupName.registration}`, undefined, { shallow: true })

    try {
      if (isSubscribed) {
        await unsubscribeWebinar(webinarId)
        dispatch({ type: Action.SET_SUBSCRIPTION, id: webinarId, status: isSubscribed })
      } else {
        await subscribeWebinar(webinarId)
        dispatch({ type: Action.SET_SUBSCRIPTION, id: webinarId, status: isSubscribed })
      }
    } catch {
      console.log('Не удается отправить запрос')
    }
  }

  const labelMods = useMemo(() => {
    const params = defineDopParams(data)
    if (params) params[SearchOpt.courses] = `(7)`
    return params
  }, [])

  return (
    <>
      <SEOMetatags data={SpecialityPageSEO} />
      <SEOBreadCrumbs data={SpecialityPageBC} />
      <MainLayout isSmooth>
        <WidthLimiter noLeftPadding noRightPadding fullHeight>
          <h1 className={styles.titleFirst}>НМО по специализации «{searchString}»</h1>
          <div>
            <div className={styles.searchPanel}>
              <SwitchToggle
                options={searchNamesSpeaciality}
                selectedOption={selectedOption}
                handleToggle={setSelectedOption}
                labelDopParams={labelMods}
              />
            </div>
          </div>
          <div ref={containerRef}>
            <ResultsWrapper
              articles={data[SearchKey[selectedOption]]?.rows}
              cutQueryWords={data.explain}
              isLoading={data[SearchKey[selectedOption]]?.isLoading}
              toggleSubscribe={handleCardData}
            />
            <SearchPagination
              maxPage={Math.ceil(data[SearchKey[selectedOption]].count / CARDS_ON_PAGE) || 0}
              currentPage={Math.floor(data[SearchKey[selectedOption]].offset / CARDS_ON_PAGE + 1)}
              handleParameterSet={handleParameterSet}
            />
          </div>
        </WidthLimiter>
      </MainLayout>
    </>
  )
}

export default SpecialitySearch
